import Vue from 'vue';
import VueRouter from 'vue-router';
import VueCookie from 'vue-cookie';

import NotFoundView from '@/views/NotFoundView';
import LoginView from '@/views/unauth/LogInView';
import ResetPasswordView from '@/views/unauth/ResetPasswordView';
import ForgotPasswordView from '@/views/unauth/ForgotPasswordView';

import HomeView from '@/views/auth/home/HomeView';

import AdminBaseControlView from '@/views/auth/admin/controls/BaseControlView';
import AdminGroupsView from '@/views/auth/admin/GroupsView';
import AdminAccountSettingsView from '@/views/auth/admin/account/AccountSettingsView';

import { DEFINE_LOADING } from '@/store/modules/app/actions';
import { DEFINE_USER, CLEAR_USER } from '@/store/modules/user/actions';


Vue.use(VueRouter);
Vue.use(VueCookie);

const routes = [
  {
    path: '/login',
    name: 'LoginView',
    component: LoginView,
    meta: { requiresUnauth: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPasswordView',
    component: ForgotPasswordView,
    meta: { requiresUnauth: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPasswordView',
    component: ResetPasswordView,
    meta: { requiresUnauth: true },
  },
  {
    path: '/home',
    name: 'HomeView',
    component: HomeView,
    meta: {
      requiresAuth: true,
      title: 'Home',
      breadcrumbs: [
        { title: 'Home', disabled: true },
      ],
    },
  },
  {
    path: '/settings/admin',
    name: 'AdminBaseControlView',
    component: AdminBaseControlView,
    permissions: ['anchorapps:admin'],
    meta: {
      requiresAuth: true,
      title: 'Administration Controls',
      breadcrumbs: [
        { title: 'Home', to: { name: 'HomeView' } },
        { title: 'Settings', disabled: true },
        { title: 'Administration Controls', disabled: true },
      ],
    },
  },
  {
    path: '/settings/admin/users',
    redirect: { name: 'AdminBaseControlView', query: { type: 'users' } },
  },
  {
    path: '/settings/admin/groups',
    redirect: { name: 'AdminBaseControlView', query: { type: 'groups' } },
  },
  {
    path: '/settings/admin/permissions',
    redirect: { name: 'AdminBaseControlView', query: { type: 'permissions' } },
  },
  {
    path: '/settings/account',
    name: 'AdminAccountSettingsView',
    component: AdminAccountSettingsView,
    meta: {
      requiresAuth: true,
      title: 'Account Settings',
      breadcrumbs: [
        { title: 'Home', to: { name: 'HomeView' } },
        { title: 'Settings', disabled: true },
        { title: 'Account Settings', disabled: true },
      ],
    },
  },
  {
    path: '/settings/groups',
    name: 'AdminGroupsView',
    component: AdminGroupsView,
    meta: {
      requiresAuth: true,
      title: 'Group Administration',
      breadcrumbs: [
        { title: 'Home', to: { name: 'HomeView' } },
        { title: 'Settings', disabled: true },
        { title: 'Group Administration', disabled: true },
      ],
    },
  },
  {
    path: '/404',
    name: 'NotFound',
    component: NotFoundView,
    meta: {
      title: ' ',
    },
  },
  {
    path: '/',
    redirect: { name: 'HomeView' },
  },
  {
    path: '*',
    redirect: { name: 'NotFound' },
  },
];

const router = new VueRouter({
  routes,
  scrollBehavior: () => ({ x: 0, y: 0 }),
});

router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some(m => m.meta.requiresAuth);
  const requiresUnauth = to.matched.some(m => m.meta.requiresUnauth);
  const { permissions } = to.meta;
  const checkPermission = permissionToCheck => {
    const userPermissions = router.app.$store.getters.user.permissions;
    return userPermissions.includes(permissionToCheck);
  };

  const onUnauth = () => {
    if (requiresAuth) {
      next({
        name: 'LoginView',
        query: { redirect: to.fullPath },
      });
    } else {
      next();
    }
  };

  const resolveRoute = async () => {
    const getUser = () => router.app.$store.getters.user;
    try {
      let user = getUser();
      if (!user) {
        router.app.$store.dispatch(DEFINE_LOADING, { loading: true });
        const datetime = (new Date().getTime() / 1000 / 10).toFixed(0);
        const response = await router.app.$requests.get(
          `auth/userInfo?t=${datetime}`,
          { baseURL: process.env.VUE_APP_BACKEND_API },
        );
        router.app.$store.dispatch(DEFINE_USER, { user: response.data });
        user = getUser();
        router.app.$store.dispatch(DEFINE_LOADING, { loading: false });
      }

      if (requiresUnauth) {
        const redirect = to.query.next || to.query.redirect;
        if (redirect) {
          if (redirect.startsWith('http')) {
            router.app.$store.dispatch(DEFINE_LOADING, { loading: true });
            window.location.href = redirect;
            return;
          }

          next(redirect);
        } else {
          next({ name: 'HomeView' });
        }
      } else if (permissions && !permissions.some(checkPermission)) {
        next({ name: 'NotFound' });
      } else {
        next();
      }
    } catch (err) {
      router.app.$store.dispatch(DEFINE_LOADING, { loading: false });
      router.app.$store.dispatch(CLEAR_USER);
      onUnauth();
    }
  };

  if (router.app.$store.getters.hasLoggedOut) {
    onUnauth();
  } else {
    resolveRoute();
  }
});

export default router;
